/* ─────────────────────────────────────────────────────────────
   GestionEstadoChoferes.jsx — admin y supervisores de pipas
   Marca a un chofer como activo / vacaciones / incapacidad, con
   fecha de regreso. Mientras no esté "activo", el bot de WhatsApp
   no le manda la ruta del día (solo le avisa que está marcado ausente).
   ───────────────────────────────────────────────────────────── */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLES_PERMITIDOS = ["admin", "supervisor_general_pipas", "supervisor_pipas"];

const ETIQUETAS = {
  activo: { label: "Activo", color: "#15803D", bg: "#DCFCE7" },
  vacaciones: { label: "Vacaciones", color: "#9A3412", bg: "#FFEDD5" },
  incapacidad: { label: "Incapacidad", color: "#B91C1C", bg: "#FEE2E2" },
};

export default function GestionEstadoChoferes() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (!ROLES_PERMITIDOS.includes(user?.rol)) return null;

  return (
    <>
      <div className="mt-6 p-4 bg-white border border-slate-200 rounded-lg" data-testid="seccion-estado-choferes">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-bold text-slate-800">
              <i className="fa-solid fa-user-clock mr-2 text-amber-600"/>
              Estado de choferes (activo / vacaciones / incapacidad)
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Mientras un chofer no esté "activo", el bot no le manda la ruta del día.
            </div>
          </div>
          <button
            onClick={() => setOpen(true)}
            data-testid="btn-abrir-estado-choferes"
            className="bg-amber-600 hover:bg-amber-700 text-white font-bold rounded px-4 py-2 text-sm"
          >
            <i className="fa-solid fa-list-check mr-2"/> Gestionar
          </button>
        </div>
      </div>

      {open && <ModalEstadoChoferes onClose={() => setOpen(false)}/>}
    </>
  );
}


function ModalEstadoChoferes({ onClose }) {
  const [choferes, setChoferes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [editando, setEditando] = useState(null); // chofer_id

  useEffect(() => {
    cargar();
  }, []);

  const cargar = async () => {
    setCargando(true);
    try {
      const r = await axios.get(`${API}/usuarios`, { params: { rol: "chofer" } });
      setChoferes(r.data || []);
    } catch (e) {
      toast.error("No se pudo cargar la lista de choferes");
    } finally {
      setCargando(false);
    }
  };

  return (
    <div
      onClick={onClose}
      data-testid="modal-estado-choferes-bg"
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.7)",
        zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="modal-estado-choferes"
        style={{
          background: "white", borderRadius: 14, width: "100%", maxWidth: 760,
          maxHeight: "92vh", overflow: "auto",
          boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
        }}
      >
        <header style={{ padding: "16px 20px", borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#0F172A" }}>
            <i className="fa-solid fa-user-clock mr-2 text-amber-600"/>
            Estado de choferes
          </div>
          <button onClick={onClose} aria-label="Cerrar" data-testid="modal-estado-choferes-cerrar"
                  style={{ background: "transparent", border: 0, fontSize: 22, cursor: "pointer", color: "#64748B" }}>
            <i className="fa-solid fa-xmark"/>
          </button>
        </header>

        <section style={{ padding: 12 }}>
          {cargando && <div style={{ padding: 20, textAlign: "center", color: "#64748B" }}>Cargando…</div>}
          {!cargando && choferes.length === 0 && (
            <div style={{ padding: 20, textAlign: "center", color: "#64748B" }}>No hay choferes registrados.</div>
          )}
          {!cargando && choferes.map((c) => (
            <FilaChofer
              key={c.id}
              chofer={c}
              editando={editando === c.id}
              onEditar={() => setEditando(c.id)}
              onCerrarEdicion={() => setEditando(null)}
              onGuardado={cargar}
            />
          ))}
        </section>
      </div>
    </div>
  );
}


function FilaChofer({ chofer, editando, onEditar, onCerrarEdicion, onGuardado }) {
  const estadoActual = chofer.estado_disponibilidad || "activo";
  const [estado, setEstado] = useState(estadoActual);
  const [fechaRegreso, setFechaRegreso] = useState(chofer.fecha_regreso || "");
  const [guardando, setGuardando] = useState(false);
  const etiqueta = ETIQUETAS[estadoActual] || ETIQUETAS.activo;

  const guardar = async () => {
    if (estado !== "activo" && !fechaRegreso) {
      toast.error("Indica la fecha de regreso");
      return;
    }
    setGuardando(true);
    try {
      await axios.post(`${API}/choferes/${chofer.id}/estado`, {
        estado_disponibilidad: estado,
        fecha_regreso: estado !== "activo" ? fechaRegreso : null,
      });
      toast.success(`${chofer.nombre}: estado actualizado`);
      onCerrarEdicion();
      onGuardado();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 8px", borderBottom: "1px solid #F1F5F9", flexWrap: "wrap" }} data-testid={`fila-chofer-${chofer.id}`}>
      <div style={{ flex: 1, minWidth: 160, fontWeight: 700, color: "#0F172A" }}>{chofer.nombre}</div>

      {!editando && (
        <>
          <span style={{ background: etiqueta.bg, color: etiqueta.color, fontSize: 12, fontWeight: 700, padding: "4px 10px", borderRadius: 999 }}>
            {etiqueta.label}{chofer.fecha_regreso ? ` · regresa ${chofer.fecha_regreso}` : ""}
          </span>
          <button onClick={onEditar} data-testid={`btn-editar-${chofer.id}`}
                  style={{ background: "#F1F5F9", border: "1px solid #CBD5E1", borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            Cambiar
          </button>
        </>
      )}

      {editando && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <select value={estado} onChange={(e) => setEstado(e.target.value)} data-testid={`select-estado-${chofer.id}`}
                  style={{ padding: "6px 8px", borderRadius: 6, border: "1px solid #CBD5E1", fontSize: 13 }}>
            <option value="activo">Activo</option>
            <option value="vacaciones">Vacaciones</option>
            <option value="incapacidad">Incapacidad</option>
          </select>
          {estado !== "activo" && (
            <input type="date" value={fechaRegreso} onChange={(e) => setFechaRegreso(e.target.value)}
                   data-testid={`input-fecha-regreso-${chofer.id}`}
                   style={{ padding: "6px 8px", borderRadius: 6, border: "1px solid #CBD5E1", fontSize: 13 }}/>
          )}
          <button onClick={guardar} disabled={guardando} data-testid={`btn-guardar-${chofer.id}`}
                  style={{ background: "#15803D", color: "white", border: 0, borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 700, cursor: guardando ? "wait" : "pointer" }}>
            {guardando ? "Guardando…" : "Guardar"}
          </button>
          <button onClick={onCerrarEdicion} data-testid={`btn-cancelar-${chofer.id}`}
                  style={{ background: "transparent", border: "1px solid #CBD5E1", borderRadius: 6, padding: "6px 12px", fontSize: 12, cursor: "pointer" }}>
            Cancelar
          </button>
        </div>
      )}
    </div>
  );
}
